import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Briefcase, MessageSquare, User, LogIn, X } from 'lucide-react';
import { ActionButton } from '@/components/ui/action-button';
import { SharedWithMeResource } from '@/hooks/useSharedWithMe';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface SharedWithMeListProps {
  resources: SharedWithMeResource[] | undefined;
  isLoading: boolean;
}

const getResourceIcon = (type: string) => {
  switch (type) {
    case 'caso':
      return Briefcase;
    case 'chat':
      return MessageSquare;
    default:
      return Briefcase;
  }
};

const getResourceColor = (type: string) => {
  switch (type) {
    case 'caso':
      return 'text-blue-600 bg-blue-50 dark:bg-blue-950';
    case 'chat':
      return 'text-green-600 bg-green-50 dark:bg-green-950';
    default:
      return 'text-gray-600 bg-gray-50 dark:bg-gray-950';
  }
};

export function SharedWithMeList({ resources, isLoading }: SharedWithMeListProps) {
  const [removeDialog, setRemoveDialog] = useState<{
    open: boolean;
    resource: SharedWithMeResource | null;
  }>({ open: false, resource: null });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const removeAccess = useMutation({
    mutationFn: async (resource: SharedWithMeResource) => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Usuário não autenticado');

      if (resource.type === 'caso') {
        const { error } = await supabase
          .from('caso_user_shares')
          .delete()
          .eq('id', resource.id)
          .eq('shared_with_user_id', userData.user.id);

        if (error) throw error;
      } else if (resource.type === 'chat') {
        const { error } = await supabase
          .from('conversation_user_shares')
          .delete()
          .eq('id', resource.id)
          .eq('shared_with_user_id', userData.user.id);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-with-me'] });
      toast.success('Acesso removido com sucesso');
    },
    onError: () => {
      toast.error('Erro ao remover acesso');
    },
  });

  const handleRemoveClick = (resource: SharedWithMeResource) => {
    setRemoveDialog({ open: true, resource });
  };

  const handleConfirmRemove = () => {
    if (!removeDialog.resource) return;
    removeAccess.mutate(removeDialog.resource);
    setRemoveDialog({ open: false, resource: null });
  };

  const handleOpenResource = (resource: SharedWithMeResource) => {
    if (resource.type === 'caso') {
      navigate(`/casos/${resource.resourceId}`);
    } else if (resource.type === 'chat') {
      navigate(`/chat/${resource.resourceId}`);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recursos Compartilhados Comigo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Recursos Compartilhados Comigo</CardTitle>
        </CardHeader>
        <CardContent>
          {!resources || resources.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum recurso foi compartilhado com você ainda
            </p>
          ) : (
            <div className="space-y-3">
              {resources.map((resource) => {
                const Icon = getResourceIcon(resource.type);
                const colorClass = getResourceColor(resource.type);

                return (
                  <div
                    key={resource.id}
                    className="p-4 rounded-lg border hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex gap-3 flex-1">
                        <div className={`p-2 rounded-lg ${colorClass}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{resource.title}</h4>
                            <Badge variant="outline" className="text-xs">
                              {resource.type === 'caso' ? 'Caso' : 'Chat'}
                            </Badge>
                            {resource.permissionLevel && (
                              <Badge variant="secondary" className="text-xs">
                                {resource.permissionLevel === 'view' ? 'Visualizar' : 
                                 resource.permissionLevel === 'edit' ? 'Editar' : 
                                 resource.permissionLevel}
                              </Badge>
                            )}
                          </div>
                          <div className="space-y-1 text-sm text-muted-foreground">
                            <p className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              Compartilhado por: {resource.sharedBy.name}
                            </p>
                            <p>
                              {formatDistanceToNow(resource.sharedAt, {
                                addSuffix: true,
                                locale: ptBR,
                              })}
                            </p>
                            {resource.expiresAt && (
                              <p className="text-orange-600 dark:text-orange-400">
                                Expira{' '}
                                {formatDistanceToNow(resource.expiresAt, {
                                  addSuffix: true,
                                  locale: ptBR,
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <ActionButton
                          icon={LogIn}
                          label="Entrar"
                          variant="accept"
                          onClick={() => handleOpenResource(resource)}
                          className="h-9"
                        />
                        <ActionButton
                          icon={X}
                          label="Remover"
                          variant="reject"
                          onClick={() => handleRemoveClick(resource)}
                          className="h-9"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={removeDialog.open}
        onOpenChange={(open) => setRemoveDialog({ open, resource: removeDialog.resource })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Acesso</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover seu acesso a "{removeDialog.resource?.title}"?
              Você não poderá mais visualizar este recurso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemove}>
              Remover Acesso
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
